import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { getProblemTransitionVariants } from "../../utils/pageTransition";
import api from '../../api/apiHelper';
import axios from 'axios';
import { RenderMarkdownWithLatex } from '../../utils/utils';
import { AUTH_TOKEN_KEY } from '../../utils/dashboardUtils';
import { useWindowSize } from '../../hooks/useWindowSize';
import { useAiChat } from '../../hooks/useAiChat';
import { triggerHaptic } from '../../utils/haptics';
import { trackFirstDailyGoal, trackSecondDayStreak } from '../../api/tiktokEvents';
import { 
    AiOutlineQuestionCircle, 
    AiOutlineArrowLeft,
    AiOutlineCheckCircle,
    AiOutlineCloseCircle, 
    AiOutlineExclamationCircle
} from "react-icons/ai"; 
import { FaLock, FaPencilAlt, FaRegLightbulb, FaRegFileAlt } from 'react-icons/fa';
import { FiChevronRight, FiChevronDown } from 'react-icons/fi';
import orzelLogo from "../../img/orzel_logo_192.png";

// Importy komponentów UI
import PageTestPage from '../molecules/PageTestPage';
import { readStoredStatus } from '../molecules/Page';
import AiChatPopup from '../molecules/AiChatPopup';
import DailyGoalSuccessPopup from '../molecules/DailyGoalSuccessPopup';
import TrainingProblemsModal from '../molecules/TrainingProblemsModal';
import AttemptSummaryModal from '../molecules/AttemptSummaryModal';
import PaywallPopup from "../molecules/PaywallPopup"; 
import SolutionStepper from "../molecules/SolutionStepper";
import HintAndFormulas from "../molecules/HintAndFormulas";
import ScratchpadModal from "../molecules/ScratchpadModal";
import HintPopup from "../molecules/HintPopup"; // <--- IMPORT POPUPA
import { invalidateApiCache } from '../../utils/apiCache';
import { isAccessBlocked } from '../../utils/access';

// --- ANIMACJE ---
// --- ANIMACJA PANELU (FEEL Z NAWIGACJI: FADE + LEKKIE UNIESIENIE) ---
// Przejście między zadaniami = getProblemTransitionVariants (wspólny moduł, spring + subtelny scale,
// spójne z nawigacją, lekko spokojniejsze). Definiowane w komponencie z useReducedMotion.

// --- ANIMACJA FEEDBACKU ODPOWIEDZI (POP / SHAKE) — jak w SolveProblem ---
const answerFeedbackVariants = {
    idle: { scale: 1, x: 0 },
    correct: { scale: [1, 1.06, 1], transition: { duration: 0.4, ease: "easeOut", times: [0, 0.4, 1] } },
    incorrect: { x: [0, -8, 8, -6, 6, -3, 0], transition: { duration: 0.4, ease: "easeInOut" } }
};

// Komponent statusu
const ProblemStatusBadge = ({ problem, answer }) => {
    const baseClass = "flex justify-center items-center gap-2 text-sm font-semibold py-2";

    if (!answer) {
        return (
            <div className={`${baseClass} text-gray-400`}>
                <AiOutlineQuestionCircle className="w-4 h-4" />
                <span>Oczekuje na rozwiązanie</span>
            </div>
        );
    }

    if (answer.is_skipped) {
        return (
            <div className={`${baseClass} text-red-600`}>
                <AiOutlineCloseCircle className="w-4 h-4" />
                <span>Pominięto (0 pkt)</span>
            </div>
        );
    }

    const maxPts = parseInt(problem.max_points, 10); 
    if (problem.is_open) {
        const awardedPts = parseInt(answer.points_awarded, 10);
        if (awardedPts === maxPts) {
            return (
                <div className={`${baseClass} text-green-600`}>
                    <AiOutlineCheckCircle className="w-4 h-4" />
                    <span>Rozwiązane ({awardedPts} / {maxPts} pkt)</span>
                </div>
            );
        }
        if (awardedPts > 0) {
            return (
                <div className={`${baseClass} text-yellow-600`}>
                    <AiOutlineExclamationCircle className="w-4 h-4" />
                    <span>Częściowo rozwiązane ({awardedPts} / {maxPts} pkt)</span>
                </div>
            );
        }
        return (
            <div className={`${baseClass} text-red-600`}>
                <AiOutlineCloseCircle className="w-4 h-4" />
                <span>Nieprawidłowo rozwiązane (0 / {maxPts} pkt)</span>
            </div>
        );
    } 
    else {
        if (answer.selected_answer === problem.true_answer) {
             return (
                <div className={`${baseClass} text-green-600`}>
                    <AiOutlineCheckCircle className="w-4 h-4" />
                    <span>Poprawna odpowiedź (1 pkt)</span>
                </div>
            );
        } 
        else {
             return (
                <div className={`${baseClass} text-red-600`}>
                    <AiOutlineCloseCircle className="w-4 h-4" />
                    <span>Nieprawidłowa odpowiedź (0 pkt)</span>
                </div>
            );
        }
    }
};

const PointsSelector = ({ maxPoints, value, onChange, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const options = Array.from({ length: maxPoints + 1 }, (_, i) => i);
    const hasValue = value !== '' && value !== undefined && value !== null;

    if (disabled) {
        return (
            <div className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-500">
                {hasValue ? `${value} / ${maxPoints} pkt` : 'Brak oceny'}
            </div>
        );
    }

    return (
        <div className="relative w-full" ref={ref}>
            <button
                onClick={() => setIsOpen(prev => !prev)}
                className={`w-full flex items-center justify-between gap-3 bg-white px-4 py-3 rounded-xl shadow-sm transition-all border ${isOpen ? 'border-[#233855] shadow-md' : 'border-gray-200 hover:border-[#233855]/40 hover:shadow-md'}`}
            >
                <span className={`text-sm font-medium ${hasValue ? 'text-[#233855] font-bold' : 'text-gray-400'}`}>
                    {hasValue ? `${value} / ${maxPoints} pkt` : 'Wybierz punkty...'}
                </span>
                <div className={`w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center transition-transform ${isOpen ? 'rotate-180 bg-[#233855]/10' : ''}`}>
                    <FiChevronDown className="w-3.5 h-3.5 text-gray-500" />
                </div>
            </button>
            {isOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-[#233855]/20 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-150">
                    {options.map(num => (
                        <button
                            key={num}
                            onClick={() => { onChange(String(num)); setIsOpen(false); }}
                            className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${String(value) === String(num) && hasValue ? 'bg-[#233855] text-white font-semibold' : 'text-gray-700 hover:bg-[#233855]/5'}`}
                        >
                            {num} pkt
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const ExamSheetResolver = ({ examAttemptData, setExamAttemptData }) => {
    const navigate = useNavigate();
    const { width } = useWindowSize();
    const apiUrl = process.env.REACT_APP_API_SOURCE || "https://matematly.pl/api/";

    // Stany Główne Arkusza
    const [attemptData, setAttemptData] = useState(() => {
        if (examAttemptData) return examAttemptData;
        const savedData = localStorage.getItem('examAttemptData');
        return savedData ? JSON.parse(savedData) : null;
    }); 
    // Start od razu na pierwszym zadaniu bez odpowiedzi — wcześniej efekt przeskakiwał
    // z zadania 1 już po narysowaniu (pełna animacja wyjścia i wejścia).
    const [currentProblemIndex, setCurrentProblemIndex] = useState(() => {
        const idx = attemptData?.problems?.findIndex(p => !p.attempt_answer) ?? -1;
        return idx >= 0 ? idx : 0;
    });
    const startTimeRef = useRef(Math.floor(Date.now() / 1000));

    // Stany Przeportowane
    const [isHintUsed, setIsHintUsed] = useState(false);
    const [showSolution, setShowSolution] = useState(false);
    const [selectedAnswerText, setSelectedAnswerText] = useState(null);
    const [accountStatus, setAccountStatus] = useState(readStoredStatus);
    const [popupState, setPopupState] = useState({ isOpen: false, completionLevel: 0 });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [addTopicStatus, setAddTopicStatus] = useState({ loading: false, added: false });
    const [tempPoints, setTempPoints] = useState('');
    const [summaryAttemptId, setSummaryAttemptId] = useState(null);
    const [isNavigating, setIsNavigating] = useState(false);
    const prefersReducedMotion = useReducedMotion();
    const problemTransitionVariants = getProblemTransitionVariants(prefersReducedMotion);

    // Stan Paywalla
    const [isPaywallPopupOpen, setPaywallPopupOpen] = useState(false);
    const [popupUserData] = useState(() => {
        try { return JSON.parse(localStorage.getItem("paywallUserData")); } catch { return null; }
    });

    // --- SZKICOWNIK & HINTY ---
    const [sketches, setSketches] = useState({});
    const [isScratchpadOpen, setIsScratchpadOpen] = useState(false);
    const [isHintPopupOpen, setIsHintPopupOpen] = useState(false); // <--- STAN POPUPA

    // Zmienne Pochodne
    const currentProblem = attemptData ? attemptData.problems[currentProblemIndex] : null;
    const savedAnswer = currentProblem?.attempt_answer;
    const isAnswerChecked = !!savedAnswer;
    const isReviewMode = attemptData?.status === 'completed';

    // Hook AI
    const {
        isChatOpen, chatHistory, isResponding, remainingCalls, maxCalls,
        handleOpenChat, handleSendMessage, setIsChatOpen, resetChat
    } = useAiChat(currentProblem, selectedAnswerText);

    // Efekty
    useEffect(() => {
        if (!attemptData) navigate('/start-exam');
    }, [attemptData, navigate]);

    useEffect(() => {
        startTimeRef.current = Math.floor(Date.now() / 1000);
        setShowSolution(false);
        setIsHintUsed(false);
        setSelectedAnswerText(null);
        setAddTopicStatus({ loading: false, added: false }); 
        setTempPoints(savedAnswer?.points_awarded ?? '');
        if (currentProblem) {
            resetChat(); 
        }
    }, [currentProblemIndex, currentProblem, resetChat, savedAnswer]); 

    useEffect(() => {
    if (!isScratchpadOpen) {
        const el = document.getElementById('scratchpad-body-lock');
        if (el) el.remove();
        document.body.style.overflow = '';
    }
}, [isScratchpadOpen]);

    const maxTaskNumber = useMemo(() => {
        if (!attemptData || !attemptData.problems || attemptData.problems.length === 0) return 0;
        const lastProblem = attemptData.problems[attemptData.problems.length - 1];
        const rawNum = lastProblem.display_number || lastProblem.problem_number;
        return String(rawNum).split('.')[0];
    }, [attemptData]);

    const dailyGoalProgress = accountStatus && accountStatus.problems_daily_goal > 0
        ? Math.min(100, (accountStatus.problems_solved_today / accountStatus.problems_daily_goal) * 100 + 5)
        : 0;
    
    const getVisualLengthEstimate = (text) => {
        if (!text) return 0;
        const sanitizedText = text.replace(/\\[a-zA-Z]+|\$|{|}/g, '');
        return sanitizedText.length;
    };
    
    const gridClass = useMemo(() => {
        if (!currentProblem || currentProblem.is_open) return "grid-cols-1";
        let dynamicThreshold = width < 768 ? 15 : 40;
        const isAnyAnswerLong = currentProblem.answers.some(answer => getVisualLengthEstimate(answer) > dynamicThreshold);
        return isAnyAnswerLong ? "grid-cols-1" : "grid-cols-2";
    }, [currentProblem, width]);


    // --- Handlery ---
    
    const handleSaveSketch = (problemId, paths) => {
        setSketches(prev => ({
            ...prev,
            [problemId]: paths
        }));
    };

    const goToProblem = (index) => {
        if (isNavigating) return; 
        
        if (index >= 0 && index < attemptData.problems.length) {
            setIsNavigating(true);
            setCurrentProblemIndex(index);
        }
    };
    
    const handleAddSubfieldToTraining = async () => {
        if (!currentProblem || addTopicStatus.added || addTopicStatus.loading) return; 
        setAddTopicStatus({ loading: true, added: false });

        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) {
            console.error("Brak tokena, nie można dodać tematu.");
            setAddTopicStatus({ loading: false, added: false });
            return; 
        }

        try {
            await axios.post(
                `${apiUrl}exams/training/add_subfield/`,
                // problem_id jest potrzebny na rozszerzeniu — koszyk trzyma tam
                // szablony, a szablon zna tylko konkretne zadanie, nie podtemat.
                { subfield_id: currentProblem.subfield_id, problem_id: currentProblem.problem_id },
                { headers: { Authorization: `Token ${token}` } }
            );
            setAddTopicStatus({ loading: false, added: true });
            // Temat wyladowal juz w profilu (selected_custom_subfields). Training synchronizuje
            // swoj koszyk z serwera TYLKO gdy jego lokalny cache 'training_basket_details' jest
            // pusty, wiec czyscimy go + odswiezamy dashboard (zrodlo selected_custom_subfields_details,
            // z ktorego Training bierze koszyk wraz z nazwa tematu). Bez tego temat nie pokaze sie w Treningu.
            invalidateApiCache('training_basket_details');
            invalidateApiCache('user_dashboard_data');
        } catch (error) {
            console.error("Błąd dodawania tematu:", error);
            setAddTopicStatus({ loading: false, added: false });
        }
    };

    const handleSaveAnswer = (answerPayload) => {
        if (isReviewMode) return; 
        
        const newProblems = [...attemptData.problems];
        newProblems[currentProblemIndex] = {
            ...newProblems[currentProblemIndex],
            attempt_answer: {
                selected_answer: answerPayload.selected_answer,
                points_awarded: answerPayload.points_awarded,
                is_skipped: answerPayload.is_skipped,
            }
        };
        
        const newAttemptData = { ...attemptData, problems: newProblems };
        setAttemptData(newAttemptData);
        setExamAttemptData(newAttemptData);
        localStorage.setItem('examAttemptData', JSON.stringify(newAttemptData));

        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) return;

        const fullPayload = {
            attempt_id: attemptData.attempt_id,
            problem_id: currentProblem.problem_id,
            field_id: currentProblem.field_id,
            ...answerPayload,
        };

        axios.post(
            `${apiUrl}exams/attempt/save_answer/`, 
            fullPayload,
            { headers: { Authorization: `Token ${token}` } }
        )
        .then(response => {
            const data = response.data;

            // Cel dzienny da sie domknac takze na arkuszu — backend liczy go tu tak samo
            // jak w sesji nauki (`current_streak` juz po inkrementacji), wiec bez tego
            // kto konczy cel na arkuszu wypadal z lejka TikToka.
            if (data.daily_goal_completed) {
                const userId = localStorage.getItem("user_id");
                if (data.current_streak === 1) {
                    trackFirstDailyGoal(userId);
                } else if (data.current_streak === 2) {
                    trackSecondDayStreak(userId);
                }
            }

            if (data.daily_goal_completed && accountStatus && accountStatus.problems_daily_goal > 0) {
                const solvedToday = data.problems_solved_today;
                const dailyGoal = accountStatus.problems_daily_goal;
                if (solvedToday === dailyGoal || solvedToday === dailyGoal * 2 || solvedToday === dailyGoal * 3) {
                    const completionLevel = Math.floor(solvedToday / dailyGoal);
                    setPopupState({ isOpen: true, completionLevel: completionLevel });
                }
                setAccountStatus(prev => ({...prev, problems_solved_today: solvedToday, current_streak: data.current_streak}));
            }
        })
        .catch(error => {
            console.error("Błąd zapisu w tle:", error);
        });
    };

    const handleSaveOpenAnswer = () => {
        if (isReviewMode) return; 
        let points = parseInt(tempPoints, 10); 
        const maxPoints = parseInt(currentProblem.max_points, 10); 
        if (isNaN(points) || points < 0) points = 0; 
        if (points > maxPoints) points = maxPoints;
        
        setTempPoints(points.toString()); 
        
        const isSolved = (points === maxPoints);
        handleSaveAnswer({
            selected_answer: null,
            points_awarded: points,
            is_skipped: false,
            is_solved: isSolved, 
            start_time: startTimeRef.current,
            end_time: Math.floor(Date.now() / 1000),
            is_hint_used: isHintUsed, 
            is_solution_viewed: true, 
        });
    };

    const handleHint = () => {
        setIsHintUsed(true);
    };

    // --- NOWE HANDLERY HINTÓW ---
    const handleLightbulbClick = () => {
        handleHint(); 
        setIsHintPopupOpen(true);
    };

    const handleFormulaClick = () => {
        handleHint(); 
    };

    const handleSolution = () => {
        if (isAnswerChecked) {
            setShowSolution(true);
            return;
        }
    };

    const onSelectAnswer = (answerText) => {
        if (isAnswerChecked || isReviewMode) return; 
        
        setSelectedAnswerText(answerText);
        const isSolved = answerText === currentProblem.true_answer;
        const points = isSolved ? 1 : 0;

        triggerHaptic(isSolved ? "success" : "error");

        handleSaveAnswer({
            selected_answer: answerText,
            points_awarded: points, 
            is_skipped: false,
            is_solved: isSolved,
            start_time: startTimeRef.current,
            end_time: Math.floor(Date.now() / 1000),
            is_hint_used: isHintUsed, 
            is_solution_viewed: showSolution, 
        });
    };

    const handleCompleteAttempt = async () => {
        if (window.confirm("Czy na pewno chcesz zakończyć i ocenić tę próbę? Nie będzie można jej już edytować.")) {
            try {
                await api.post('exams/attempt/complete/', { 
                    attempt_id: attemptData.attempt_id 
                });
                setSummaryAttemptId(attemptData.attempt_id);
            } catch (error) {
                console.error("Błąd podczas kończenia próby:", error);
                alert("Nie udało się zakończyć próby. Spróbuj ponownie.");
            }
        }
    };

    const handleCloseSummary = () => {
        setSummaryAttemptId(null);
        invalidateApiCache('user_dashboard_data');
        invalidateApiCache('exams');
        invalidateApiCache('user_stats');
        navigate('/start-exam');
    };

    const handleSubscribeClick = () => {
        setPaywallPopupOpen(false);
        navigate("/upgrade"); 
    };
    
    // --- Renderowanie ---

    const renderActionToolbar = () => {
        const hasAccess = !isAccessBlocked(accountStatus);

        if (!hasAccess) {
            return (
                <div className="mt-4 pt-4 border-t border-gray-200 text-center animate-fade-in">
                    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 shadow-inner">
                        <div className="flex justify-center mb-2">
                            <div className="bg-gray-200 p-2 rounded-full">
                                <FaLock className="w-5 h-5 text-gray-500" />
                            </div>
                        </div>
                        <h3 className="font-semibold text-gray-800 text-sm mb-1 uppercase font-apotek tracking-wider">
                            Twój darmowy dostęp dobiegł końca
                        </h3>
                        <p className="text-xs text-gray-500 mb-4 max-w-xs mx-auto">
                            Odblokuj wyjaśnienia AI, zadania treningowe i zapisywanie trudnych tematów.
                        </p>
                        <button
                            onClick={() => setPaywallPopupOpen(true)}
                            className="w-full sm:w-auto px-6 py-2 bg-[#E09F2E] text-white font-apotek uppercase shadow-md tracking-widest text-xs lg:text-sm rounded-lg hover:bg-yellow-600 transition-colors"
                        >
                            Przedłuż dostęp
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-3">
                <button
                    onClick={handleOpenChat}
                    disabled={isResponding}
                    className="group w-full flex items-center gap-3 bg-white border border-[#E09F2E]/40 px-4 py-3 rounded-xl shadow-sm hover:shadow-md hover:border-[#E09F2E] transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <div className="w-9 h-9 rounded-full bg-[#233855] flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <img src={orzelLogo} alt="Orzeł AI" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                        <span className="block text-sm font-bold text-[#233855] font-apotek leading-tight">Zapytaj Orła AI</span>
                        <span className="block text-xs text-gray-400 leading-tight">Wyjaśni krok po kroku</span>
                    </div>
                    <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center flex-shrink-0 transition-colors">
                        <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all" />
                    </div>
                </button>

                <div className="flex items-center gap-3 pt-2">
                    <span className="flex-grow border-t border-gray-200"></span>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Utrwalanie
                    </span>
                    <span className="flex-grow border-t border-gray-200"></span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button 
                        onClick={() => setIsModalOpen(true)}
                        className="w-full px-3 py-2 bg-[#233855] text-slate-100 font-apotek uppercase shadow-lg tracking-widest text-xs lg:text-xs rounded-lg hover:bg-[#345081] disabled:bg-gray-300"
                    >
                        Rozwiąż podobne
                    </button>
                    
                    <button
                        onClick={handleAddSubfieldToTraining}
                        disabled={addTopicStatus.loading || addTopicStatus.added}
                        className={`w-full px-3 py-2 text-slate-100 font-apotek uppercase shadow-lg tracking-widest text-xs lg:text-xs rounded-lg transition-colors ${
                            addTopicStatus.added 
                                ? 'bg-[#007D00]' 
                                : 'bg-[#233855] hover:bg-[#345081] disabled:bg-gray-300'
                        }`}
                    >
                        {addTopicStatus.loading ? '...' : (addTopicStatus.added ? 'Dodano Temat!' : 'Dodaj do treningu dowolnego')}
                    </button>
                </div>
            </div>
        );
    };


    if (!currentProblem) {
        return (
            <PageTestPage showNav={false} accountStatus={accountStatus} dailyGoalProgress={dailyGoalProgress}>
                <div className="text-center py-10"><p className="text-xl text-gray-600">Ładowanie...</p></div>
            </PageTestPage>
        );
    }

    const renderOpenTaskAnswer = () => {
        const shouldShowSolution = showSolution || isReviewMode;
        const maxPointsInt = parseInt(currentProblem.max_points, 10);

        if (shouldShowSolution) {
            return null; // treść przeniesiona poza box
        }

        if (isAnswerChecked) {
            return (
                <div className="space-y-4">
                    <PointsSelector
                        maxPoints={maxPointsInt}
                        value={savedAnswer?.points_awarded ?? ''}
                        onChange={() => {}}
                        disabled={true}
                    />
                    <button
                        onClick={() => setShowSolution(true)}
                        className="w-full bg-[#E09F2E] text-white font-apotek uppercase shadow-md tracking-widest text-sm py-2 rounded-md hover:bg-yellow-600"
                    >
                        Pokaż rozwiązanie i kryteria
                    </button>
                </div>
            );
        }

        return (
            <button
                onClick={() => setShowSolution(true)}
                className="w-full bg-[#E09F2E] text-white font-apotek shadow-md tracking-widest text-sm py-2 rounded-md hover:bg-yellow-600"
            >
                Pokaż rozwiązanie i oceń
            </button>
        );
    };


    return (
        <PageTestPage
            showNav={false}
            reportIssueData={{ problem_id: currentProblem.problem_id, field_id: currentProblem.field_id }}
            accountStatus={accountStatus}
            dailyGoalProgress={dailyGoalProgress}
        >
            {/* --- Popupy --- */}
            {popupState.isOpen && (
                <DailyGoalSuccessPopup
                    completionLevel={popupState.completionLevel}
                    onClose={() => setPopupState({ isOpen: false, completionLevel: 0 })}
                    streakDay={accountStatus?.current_streak ?? 0}    // ← DODAJ
                />
            )}
            
            {/* PAYWALL POPUP */}
            <PaywallPopup
                isOpen={isPaywallPopupOpen}
                onClose={() => setPaywallPopupOpen(false)}
                onSubscribe={handleSubscribeClick}
                userData={popupUserData} 
            />

            <AiChatPopup
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                chatHistory={chatHistory}
                onSendMessage={handleSendMessage}
                isResponding={isResponding}
                problemQuestion={currentProblem?.question}
                remainingCalls={remainingCalls}
                maxCalls={maxCalls}
            />
            <TrainingProblemsModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                sourceProblemId={currentProblem.problem_id}
            />
            <AttemptSummaryModal 
                isOpen={!!summaryAttemptId}
                onClose={handleCloseSummary}
                attemptId={summaryAttemptId}
            />

            {/* --- MODAL SZKICOWNIKA --- */}
            {currentProblem && (
                <ScratchpadModal
                    isOpen={isScratchpadOpen}
                    onClose={() => setIsScratchpadOpen(false)}
                    problem={currentProblem}
                    initialPaths={sketches[currentProblem.problem_id]} 
                    onSave={handleSaveSketch}
                />
            )}

            <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-4 lg:px-0">
                
                <div className="w-full flex mt-[3vh] lg:mt-[8vh] mb-2">
                    <button 
                        onClick={() => navigate('/start-exam')}
                        className="text-gray-500 hover:text-gray-800 transition-colors flex items-center gap-1 text-sm font-medium"
                    >
                        <AiOutlineArrowLeft /> Wróć do listy arkuszy
                    </button>
                </div>
                
                {/* --- Przyciski Nawigacji GÓRNE --- */}
                {currentProblemIndex < attemptData.problems.length - 1 && (
                    <div className="w-full pt-4 flex justify-between items-center">
                        <button 
                            onClick={() => goToProblem(currentProblemIndex - 1)}
                            disabled={currentProblemIndex === 0}
                            className="flex lg:w-[8rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus:bg-[#233855] disabled:bg-gray-300 focus:outline-none focus:ring-0 transition-colors"
                        >
                            WSTECZ
                        </button>
                        <button 
                            onClick={() => goToProblem(currentProblemIndex + 1)}
                            disabled={isNavigating}
                            className="flex lg:w-[8rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus:bg-[#233855] disabled:bg-gray-300 focus:outline-none focus:ring-0 transition-colors"
                        >
                            DALEJ
                        </button>
                    </div>
                )}
                
                {currentProblemIndex === attemptData.problems.length - 1 && (
                    <div className="w-full pt-4 flex justify-between items-center">
                        <button 
                            onClick={() => goToProblem(currentProblemIndex - 1)}
                            disabled={currentProblemIndex === 0}
                            className="flex lg:w-[8rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus:bg-[#233855] disabled:bg-gray-300 focus:outline-none focus:ring-0 transition-colors"
                        >
                            WSTECZ
                        </button>
                        
                        {!isReviewMode && (
                            <button
                                onClick={handleCompleteAttempt}
                                className="flex lg:w-[12rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus:bg-[#233855] focus:outline-none focus:ring-0 transition-colors"
                            >
                                ZAKOŃCZ PRÓBĘ
                            </button>
                        )}
                    </div>
                )}


                {/* --- Główna Karta (BIAŁE TŁO - STATYCZNE) --- */}
                {/* [ZMIANA] relative dla Żarówki */}
                {/* --- PANEL ZADANIA — CAŁOŚĆ (karta + rozwiązanie + akcje) PRZESUWA SIĘ RAZEM --- */}
                {/* Pager: przesuw przycina overflow-x-hidden na <main> (PageTestPage). Wrapper bez paddingu = karta wyśrodkowana, cień nietknięty. */}
                <div className="relative w-full mt-4">
                <AnimatePresence mode="wait" initial={false}>
                <motion.div
                    key={currentProblem.problem_id}
                    variants={problemTransitionVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    onAnimationComplete={() => setIsNavigating(false)}
                    className="w-full"
                >
                {/* --- BIAŁA KARTA (TREŚĆ ZADANIA) --- */}
                <div className="bg-white rounded-xl lg:rounded-[3rem] shadow-[4px_4px_12px_rgba(0,0,0,0.30)] border border-gray-200 py-6 px-5 lg:px-14 w-full overflow-hidden relative">

                    {/* --- IKONA ŻARÓWKI (POPUP HINT - STYL MATEMATLY GOLD) --- */}
                    {currentProblem.hint && currentProblem.hint.trim() !== "" && (
                        <button
                            onClick={handleLightbulbClick}
                            className="absolute top-4 right-4 text-[#E09F2E] hover:text-[#b88226] hover:scale-110 transition-all p-2 z-10"
                            aria-label="Zobacz wskazówkę"
                        >
                            <FaRegLightbulb size={24} strokeWidth={1} className="drop-shadow-sm" />
                        </button>
                    )}

                    {/* --- WARSTWA TREŚCI (układ pionowy) --- */}
                    <div className="w-full space-y-6">

                            {/* --- HEADER ZADANIA --- */}
                            <div className="w-full flex justify-center items-baseline flex-wrap gap-x-3 sm:gap-x-4 gap-y-2 text-xs sm:text-base font-apotek uppercase tracking-wide text-gray-700 lg:mt-[0.5vh]">
                                <span className="uppercase font-thin text-xs lg:text-xs text-[#233855]">
                                    {attemptData.sheet_name}
                                </span>
                                
                                {currentProblem.source_info && (
                                    <>
                                        <span className="text-gray-400 font-thin text-xs lg:text-xs">|</span>
                                        <span className="font-thin text-xs lg:text-xs text-gray-600">
                                            {currentProblem.source_info}
                                        </span>
                                    </>
                                )}

                                <span className="text-gray-400 font-thin text-xs lg:text-xs ">|</span>
                                
                                <span className="font-thin text-xs lg:text-xs text-[#8A0000]">
                                    Zadanie {currentProblem.display_number || currentProblem.problem_number} / {maxTaskNumber}
                                </span>
                            </div>

                            {/* --- WYKLUCZENIE (INFO) --- */}
                            {currentProblem.is_excluded && (
                                <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-center text-sm font-medium mb-4">
                                    ⚠️ To zadanie zostało wycofane z wymagań CKE do matury od 2025 roku. 
                                    Możesz je rozwiązać dla treningu, ale punkty nie wliczą się do wyniku końcowego.
                                </div>
                            )}

                            {/* --- TREŚĆ PYTANIA --- */}
                            <div className="text-base lg:text-normal font-normal text-gray-900 text-center min-h-[50px] flex flex-col items-center justify-center lg:mt-0">
                                <RenderMarkdownWithLatex text={`(${parseInt(currentProblem.max_points, 10)} pkt) ${currentProblem.question}`} />
                            </div>

                            {/* --- OBRAZEK --- */}
                            {currentProblem.image_link && (
                                <div className="flex justify-center py-2">
                                    <img 
                                            src={
                                                currentProblem.image_link.startsWith('http') 
                                                ? currentProblem.image_link 
                                                : `${process.env.REACT_APP_STATIC_FILES_SOURCE || ''}/images/${currentProblem.image_link}`
                                            } 
                                            alt="Ilustracja zadania" 
                                            className="max-w-full h-auto rounded" 
                                    />
                                </div>
                            )}

                            {/* --- KARTA WZORÓW (NAD ODPOWIEDZIAMI) --- */}
                            <div className="w-full">
                                {(() => {
                                    const hasLinkedFormulas = currentProblem.linked_formulas && currentProblem.linked_formulas.length > 0;
                                    
                                    if (!hasLinkedFormulas) return null;

                                    if (isHintUsed) {
                                        return (
                                             <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                                <HintAndFormulas 
                                                    hintText={null} 
                                                    formulas={currentProblem.linked_formulas} 
                                                />
                                            </div>
                                        );
                                    } else {
                                        // PRZYCISK "LEGALNA ŚCIĄGA" - NOWY DESIGN
                                        return (
                                            <button
                                                onClick={handleFormulaClick}
                                                className="group w-full flex items-center gap-3 bg-white border border-[#E09F2E]/40 px-4 py-3 rounded-xl shadow-sm hover:shadow-md hover:border-[#E09F2E] transition-all active:scale-[0.98] mb-4"
                                            >
                                                <div className="w-9 h-9 rounded-lg bg-[#233855] flex items-center justify-center flex-shrink-0">
                                                    <FaRegFileAlt size={16} className="text-[#E09F2E]" />
                                                </div>
                                                <div className="flex-1 text-left min-w-0">
                                                    <span className="block text-sm font-bold text-[#233855] font-apotek leading-tight">Karta Wzorów</span>
                                                    <span className="block text-xs text-gray-400 leading-tight">Otwórz wzory do zadania</span>
                                                </div>
                                                <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center flex-shrink-0 transition-colors">
                                                    <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all" />
                                                </div>
                                            </button>
                                        );
                                    }
                                })()}
                            </div>

                            {/* --- ODPOWIEDZI (ZAMKNIĘTE) --- */}
                            {!currentProblem.is_open && (
                                <div className={`grid ${gridClass} gap-2 w-full`}>
                                    {currentProblem.answers.map((answer, index) => {
                                        const isCorrectAndChecked = isAnswerChecked && currentProblem.true_answer === answer;
                                        const isIncorrectSelected = isAnswerChecked && !isCorrectAndChecked && savedAnswer?.selected_answer === answer;
                                        let buttonClass = "flex items-center justify-center rounded-lg text-[0.8rem] text-slate-100 px-3 py-1.5 font-normal tracking-wider shadow-md transition-colors duration-200 disabled:cursor-not-allowed min-h-[20px] w-full";
                                        if (isCorrectAndChecked) buttonClass += " bg-[#007D00] ring-2 ring-offset-2 ring-[#007D00]";
                                        else if (isIncorrectSelected) buttonClass += " bg-[#8A0000] opacity-90";
                                        else if (isAnswerChecked) buttonClass += " bg-[#233855] opacity-40";
                                        else buttonClass += " bg-[#233855] hover:bg-[#345081]";

                                        const feedbackState = (prefersReducedMotion || isReviewMode)
                                            ? "idle"
                                            : isCorrectAndChecked
                                                ? "correct"
                                                : isIncorrectSelected
                                                    ? "incorrect"
                                                    : "idle";

                                        return (
                                            <motion.button
                                                key={index}
                                                onClick={() => onSelectAnswer(answer)}
                                                disabled={isAnswerChecked || isReviewMode}
                                                className={buttonClass}
                                                variants={answerFeedbackVariants}
                                                animate={feedbackState}
                                                whileTap={!(isAnswerChecked || isReviewMode) ? { scale: 0.96 } : undefined}
                                            >
                                                <RenderMarkdownWithLatex text={answer} />
                                            </motion.button>
                                        );
                                    })}
                                </div>
                            )}
                            
                            {/* --- ROZWIĄZANIE (ZAMKNIĘTE) - przycisk wewnątrz boxa --- */}
                            {!currentProblem.is_open && !showSolution && !(isAnswerChecked && isReviewMode) && (
                                <button
                                    onClick={handleSolution}
                                    disabled={!isAnswerChecked}
                                    className="w-full text-xs lg:text-xs font-uppercase font-apotek shadow-md tracking-widest bg-[#F0F0F0] border border-gray-300 text-gray-600 hover:bg-gray-50 px-4 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    ROZWIĄZANIE
                                </button>
                            )}
                            
                            {currentProblem.is_open && renderOpenTaskAnswer()}
                            
                            {/* --- STATUS BADGE ( TEŻ ANIMOWANY ) --- */}
                            <div className="mt-8 pt-4 border-t border-gray-100 w-full flex justify-center">
                                <ProblemStatusBadge problem={currentProblem} answer={savedAnswer} />
                            </div>

                        </div>
                </div>
                {/* --- /BIAŁA KARTA --- */}

                {/* POZA KARTĄ, ale W TYM SAMYM PANELU: kroki rozwiązania + akcje (zamknięte zadania) */}
                {!currentProblem.is_open && (showSolution || (isAnswerChecked && isReviewMode)) && (
                    <div className="w-full mt-4 space-y-3 animate-in fade-in zoom-in duration-300">
                        <SolutionStepper solutionText={currentProblem.solution || "Brak rozwiązania."} />
                        {renderActionToolbar()}
                    </div>
                )}

                {/* POZA BOXEM: kroki + kryteria + punkty (otwarte zadania) */}
                {currentProblem.is_open && (showSolution || isReviewMode) && (
                    <div className="w-full mt-4 space-y-4 animate-in fade-in zoom-in duration-300">
                        <SolutionStepper solutionText={currentProblem.solution || "Brak rozwiązania."} />
                        {renderActionToolbar()}
                        {currentProblem.criteria.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                                <h3 className="font-semibold text-gray-700 mb-2 text-sm font-apotek uppercase tracking-wide">Kryteria Oceniania</h3>
                                <ul className="space-y-1 text-sm">
                                    {currentProblem.criteria.map((crit, idx) => (
                                        <li key={idx} className="flex gap-2 items-start">
                                            <strong className="whitespace-nowrap shrink-0 text-[#233855]">{parseInt(crit.points, 10)} pkt:</strong>
                                            <div className="flex-grow text-gray-700">
                                                <RenderMarkdownWithLatex text={crit.description} />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="space-y-2">
                            <PointsSelector
                                maxPoints={parseInt(currentProblem.max_points, 10)}
                                value={isAnswerChecked ? (savedAnswer?.points_awarded ?? '') : tempPoints}
                                onChange={setTempPoints}
                                disabled={isAnswerChecked || isReviewMode}
                            />
                            {!isAnswerChecked && !isReviewMode && (
                                <button
                                    onClick={handleSaveOpenAnswer}
                                    className="w-full bg-[#233855] text-white font-apotek uppercase shadow-md tracking-widest text-sm py-3 rounded-xl hover:bg-[#345081] transition-colors"
                                >
                                    Zapisz ocenę
                                </button>
                            )}
                        </div>
                    </div>
                )}
                </motion.div>
                </AnimatePresence>
                </div>
                {/* --- /PANEL ZADANIA --- */}

                <div className="flex-grow" />
                <div className="h-[15vh] flex-shrink-0" />
            </div>

            {/* --- PRZYCISK FAB OTWIERANIA SZKICOWNIKA --- */}
            <button
                onClick={() => setIsScratchpadOpen(true)}
                className="fixed bottom-12 right-4 z-40 bg-[#233855] text-white p-4 rounded-full shadow-[0_4px_14px_0_rgba(0,0,0,0.39)] hover:bg-[#345081] transition-transform hover:scale-105 active:scale-95 border-2 border-[#E09F2E]"
                aria-label="Otwórz brudnopis"
            >
                <FaPencilAlt size={20} />
            </button>
            
            {/* --- HINT POPUP (REUSABLE COMPONENT) --- */}
            <HintPopup 
                isOpen={isHintPopupOpen} 
                onClose={() => setIsHintPopupOpen(false)} 
                hintText={currentProblem.hint} 
            />

        </PageTestPage>
    );
};

export default ExamSheetResolver;